"use client";

import { useCallback, useEffect, useState } from "react";

import { useTransitionRouter } from "@ala/core/transition-router";
import { clsx } from "@ala/core/utils/clsx";

const INITIAL_PROGRESS_ON_START = 0.08;
const DELAY_BETWEEN_INCREMENTS = 200;

export function ProgressBar() {
  const { stage } = useTransitionRouter();
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  const incrementProgress = useCallback(() => {
    setProgress((progress): number => {
      if (progress >= 0.94) {
        return attractTo1(progress, 0.01);
      }

      if (progress >= 0.7) {
        return attractTo1(progress, 0.05);
      }

      if (progress >= 0.5) {
        return attractTo1(progress, 0.1);
      }

      return attractTo1(progress, 0.2);
    });
  }, []);

  useEffect(() => {
    let interval: number | null = null;

    switch (stage) {
      case "leaving": {
        setVisible(true);
        setProgress(INITIAL_PROGRESS_ON_START);

        interval = window.setInterval(() => {
          incrementProgress();
        }, DELAY_BETWEEN_INCREMENTS);

        break;
      }
      case "entering": {
        setProgress(1);
        setVisible(false);

        break;
      }
      case "none": {
        setVisible(false);
        setProgress(0);

        break;
      }
    }

    return () => {
      if (interval) window.clearInterval(interval);
    };
  }, [stage, incrementProgress]);

  const onAnimationEnd = () => {
    if (!visible) {
      setProgress(0);
    }
  };

  return (
    <div className="pointer-events-none fixed top-0 right-0 left-0 z-50 h-0.5 duration-200">
      <div
        className={clsx(
          "h-full bg-malachite-600 transition-[width]",
          visible ? "animate-[fade-in_0.2s_forwards]" : "animate-[fade-out_0.2s_forwards]",
        )}
        style={{
          width: `${Math.floor(progress * 100)}%`,
        }}
        onAnimationEnd={onAnimationEnd}
      >
        <div className="size-full animate-pulse shadow-sm shadow-malachite-600/80" />
      </div>
    </div>
  );
}

const attractTo1 = (value: number, strength: number) => value + (1 - value) * strength;
